/**
 * Settings → Microsoft 365 grounding (Enterprise): whether the memory also asks Microsoft 365 on a
 * person's behalf, what the deployment needs for that, and how it has gone (docs/microsoft-365.md).
 */

import { Button } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ErrorNote, inputClass, PlainPanel, StatusMark } from './ui.tsx';

export function Microsoft365GroundingPanel() {
  const view = useLoad(() => routes.microsoft365Grounding(), []);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const v = view.data;
  const toggle = async (enabled: boolean) => {
    setError(undefined);
    setBusy(true);
    try {
      await routes.setMicrosoft365Grounding(enabled);
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const steps = v
    ? [
        'Enable Work IQ for the organisation (a Global Administrator, once): provision its service principal and set a Copilot Credits spending policy in the Microsoft 365 admin centre.',
        `On the API’s own app registration (${v.consent.apiAppId ?? 'the AIOE API'}), add the delegated permission ${v.consent.scope} and grant admin consent. That lets the platform ask Work IQ as the person who signed in, with their own permissions.`,
        'Switch it on here. From then on, every question put to the memory is also put to Microsoft 365 for that person, and the cited answer is shown as its own section.',
      ]
    : [];
  return (
    <PlainPanel title="Microsoft 365 grounding">
      <ErrorNote error={error ?? view.error} />
      {v ? (
        <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-y-1.5 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]">
          <dt className="text-muted">Status</dt>
          <dd>{v.enabled ? <StatusMark state="online" label="On" /> : <StatusMark state="idle" label="Off" />}</dd>
          <dt className="text-muted">Ready</dt>
          <dd>{v.ready ? 'The platform can ask Microsoft 365 on a person’s behalf' : v.problem}</dd>
          {v.enabledAt ? (
            <>
              <dt className="text-muted">Switched on</dt>
              <dd>{timeAgo(v.enabledAt)}</dd>
            </>
          ) : null}
          <dt className="text-muted">Questions</dt>
          <dd className="tabular-nums">
            {v.questions}
            {v.lastAskedAt ? <span className="text-muted"> · last {timeAgo(v.lastAskedAt)}</span> : null}
          </dd>
          {v.lastError ? (
            <>
              <dt className="text-muted">Last problem</dt>
              <dd className="text-danger">{v.lastError}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {v ? (
        <div className="mt-3 grid gap-3">
          <p className="text-sm text-muted">When it is on, a question put to the memory is also put to Microsoft 365 Copilot as the person asking, through Work IQ. The answer is shown beside the organisation’s own, cited and labelled by Microsoft, and is never copied into the memory. Microsoft bills each question in Copilot Credits.</p>
          <div className="flex flex-wrap items-center gap-2">
            {v.enabled ? (
              <Button size="sm" variant="secondary" isPending={busy} onPress={() => void toggle(false)}>
                Switch off
              </Button>
            ) : (
              <Button size="sm" isPending={busy} isDisabled={!v.ready} onPress={() => void toggle(true)}>
                Switch on
              </Button>
            )}
            <Button size="sm" variant="ghost" onPress={() => setShowSetup((x) => !x)}>
              {showSetup ? 'Hide the set-up' : 'What it needs'}
            </Button>
          </div>
          {showSetup && (
            <ol className="grid list-decimal gap-1.5 pl-5 text-sm text-muted [overflow-wrap:anywhere]">
              {steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          )}
        </div>
      ) : null}
      {v ? <WorkbenchSignIn key={v.workbenchClientId ?? ''} current={v.workbenchClientId} onSaved={view.reload} /> : null}
    </PlainPanel>
  );
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The app AI Workbench signs people in to Microsoft 365 with, for Work IQ as a tool server: served
 * to workbenches in discovery (`microsoftSignIn`), so nobody types a client id to connect.
 */
function WorkbenchSignIn({ current, onSaved }: { current?: string; onSaved: () => void }) {
  const [draft, setDraft] = useState(current ?? '');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const value = draft.trim();
  const save = async (id: string | null) => {
    setError(undefined);
    setBusy(true);
    try {
      await routes.setWorkbenchMicrosoftClient(id);
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 grid gap-2 border-t border-separator pt-4">
      <div className="text-sm font-medium">The app AI Workbench signs in with</div>
      <p className="text-sm text-muted">
        When agents use Microsoft 365 through Work IQ, each person signs in to Microsoft on their own workbench with this app. Register it in Entra as a public client (Mobile and desktop applications, redirect <span className="mono">http://127.0.0.1</span>), add the delegated permission <span className="mono">WorkIQAgent.Ask</span> and grant admin consent. Workbenches read it from here; nobody types it.
      </p>
      <ErrorNote error={error} />
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inputClass} mono w-full max-w-sm`} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Application (client) id" aria-label="Application (client) id" spellCheck={false} />
        <Button size="sm" isPending={busy} isDisabled={!GUID.test(value) || value.toLowerCase() === current} onPress={() => void save(value)}>
          Save
        </Button>
        {current ? (
          <Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void save(null)}>
            Remove
          </Button>
        ) : null}
      </div>
      {current ? <StatusMark state="connected" label="Served to workbenches" /> : <StatusMark state="idle" label="Not set: people are asked for a client id when they connect" />}
    </div>
  );
}
